import React, { useState, useEffect, forwardRef, useImperativeHandle } from "react";
import { Form, Input, InputNumber, Row, Col, Switch } from "antd";
import { PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import CustomButton from "../../components/CustomButton";
import CustomCard from "../../components/CustomCard";
import type { OrderProductPlanVO, RiderVO } from "./orders";
import { INITIAL_PRODUCT_PLAN, INITIAL_RIDER } from "./constants";
import { getProductList, getProductInfo, COMMON_DICTS, useMetadataStore } from "@ifa/utils";
import type { ProductListVO } from "@ifa/utils";
import { MobileDatePicker } from "../../components/MobilePicker/MobileDatePicker";
import { ProductSelectInput } from "../../components/ProductSelectInput";
import { useFormConfig } from "../../hooks/useFormConfig";
import { SelectInput } from "../../components/form/SelectInput";
import { useIOSConfirm } from "../../hooks";
import { AmountInput } from "../../components/form/AmountInput";

import styles from "./ProductInsuranceForm.module.css";

export interface ProductInsuranceFormRef {
  validate: () => Promise<any>;
  addProductDetail: (productCode: string, detail: any) => void;
}

interface ProductInsuranceFormProps {
  data: OrderProductPlanVO[];
  onChange: (data: OrderProductPlanVO[]) => void;
  disableAddRemove?: boolean;
  readOnly?: boolean;
  onOpenSelector?: (context: { type: string; index?: number; rIndex?: number }, productType?: string) => void;
  activeIndex?: number;
  onRemove?: (index: number) => void;
  isFinancialInfoRequired?: boolean;
  isCorrectionMode?: boolean;
  allowMainProductSelection?: boolean;
}

export const ProductInsuranceForm = forwardRef<ProductInsuranceFormRef, ProductInsuranceFormProps>(
  (
    {
      data: rawData = [],
      onChange,
      disableAddRemove = false,
      readOnly = false,
      onOpenSelector,
      activeIndex = 0,
      onRemove,
      isFinancialInfoRequired = false,
      isCorrectionMode = false,
      allowMainProductSelection = false,
    },
    ref,
  ) => {
    const [form] = Form.useForm();
    const data = rawData.filter((item) => item && Object.keys(item).length > 0);
    // const [activeKey, setActiveKey] = useState("0");
    const [products, setProducts] = useState<ProductListVO[]>([]);
    const [productDetailsMap, setProductDetailsMap] = useState<Record<string, any>>({});
    const { confirm } = useIOSConfirm();

    const isMounted = React.useRef(false);
    const { selectMetadataList, formRequiredConfig } = useMetadataStore();
    const { isRequired, isVisible } = useFormConfig("ProductPlanForm", formRequiredConfig, data[activeIndex]);
    const { isRequired: isRiderRequired } = useFormConfig("RiderProductPlanForm", formRequiredConfig, data[activeIndex]?.riders?.[0]);
    const [datePickerVisible, setDatePickerVisible] = useState<{
      planIndex: number;
    } | null>(null);

    const getOptions = (code: string, fallback: any[] = []) => {
      const meta = selectMetadataList.find((m) => m.code === code);
      if (meta?.items?.length) {
        return meta.items.map((i) => ({
          label: i.name || i.key,
          value: i.key,
        }));
      }
      return fallback;
    };

    const currencyOptions = getOptions("CURRENCY", COMMON_DICTS.currency);
    const frequencyOptions = getOptions("FREQUENCY", COMMON_DICTS.paymentFrequency);
    const periodOptions = getOptions("PERIOD", []);
    const firstPaymentMethodOptions = getOptions("firstPaymentMethod", COMMON_DICTS.firstPaymentMethod);
    const renewalPaymentMethodOptions = getOptions("renewalPaymentMethod", COMMON_DICTS.firstPaymentMethod);
    const dividendDistributionMethodOptions = getOptions("dividendDistributionMethod", COMMON_DICTS.dividendDistributionMethod);
    const countryOptions = getOptions("country", COMMON_DICTS.country);

    const fetchProductDetail = async (code: string) => {
      if (!code || productDetailsMap[code]) return;
      try {
        const detail = await getProductInfo(code);
        if (detail) {
          setProductDetailsMap((prev) => ({ ...prev, [code]: detail }));
        }
      } catch (err) {
        console.error("Failed to fetch product info", err);
      }
    };

    useEffect(() => {
      isMounted.current = true;
      fetchProducts();
      if (data.length > 0) {
        data.forEach((item) => {
          if (item.productCode) {
            fetchProducts(item.productName || "");
            fetchProductDetail(item.productCode);
          }
          if (item.riders && item.riders.length > 0) {
            item.riders.forEach((rider) => {
              if (rider.productCode) {
                fetchProducts(rider.productName || "");
                fetchProductDetail(rider.productCode);
              }
            });
          }
        });
      }
      return () => {
        isMounted.current = false;
      };
    }, []);

    // 当产品列表加载完成后,自动填充已有产品的默认值
    const hasInitialized = React.useRef(false);
    useEffect(() => {
      if (!hasInitialized.current && products.length > 0 && data.length > 0) {
        hasInitialized.current = true;

        // 批量处理所有产品的默认值填充
        const newPlans = data.map((item) => {
          if (!item.productCode) return item;

          const selectedProduct = products.find((p) => p.productCode === item.productCode);
          if (!selectedProduct) return item;

          // 获取产品详情以获取可选项
          const productOptions = {
            currencies: selectedProduct.currencies || [],
            protectionPeriods: selectedProduct.policyPeriods || [],
            paymentTerms: selectedProduct.paymentTerm || [],
            frequencies: selectedProduct.frequencies || [],
          };

          // 自动填充只有一个选项的字段
          const autoFillData: any = {
            ...item,
            companyName: selectedProduct.companyName || selectedProduct.companyCode || item.companyName || "",
          };

          // 如果币种只有一个选项且当前未设置，自动填充
          if (productOptions.currencies.length === 1 && !item.currency) {
            autoFillData.currency = productOptions.currencies[0];
          }

          // 如果保障期只有一个选项且当前未设置，自动填充
          if (productOptions.protectionPeriods.length === 1 && !item.protectionPeriod) {
            autoFillData.protectionPeriod = productOptions.protectionPeriods[0];
          }

          // 如果供款年期只有一个选项且当前未设置，自动填充
          if (productOptions.paymentTerms.length === 1 && !item.contributionYears) {
            autoFillData.contributionYears = productOptions.paymentTerms[0];
          }

          // 如果缴费频率只有一个选项且当前未设置，自动填充
          if (productOptions.frequencies.length === 1 && !item.paymentFrequency) {
            autoFillData.paymentFrequency = productOptions.frequencies[0];
          }

          return autoFillData;
        });

        // 检查是否有变化，如果有才调用 onChange
        const hasChanges = newPlans.some((plan, index) => {
          const oldPlan = data[index];
          return (
            plan.currency !== oldPlan.currency ||
            plan.protectionPeriod !== oldPlan.protectionPeriod ||
            plan.contributionYears !== oldPlan.contributionYears ||
            plan.paymentFrequency !== oldPlan.paymentFrequency ||
            plan.companyName !== oldPlan.companyName
          );
        });

        if (hasChanges) {
          onChange(newPlans);
        }
      }
    }, [products, data, onChange]);

    useEffect(() => {
      data.forEach((item) => {
        if (item.productCode && !productDetailsMap[item.productCode]) {
          fetchProductDetail(item.productCode);
        }
      });
    }, [data]);

    useEffect(() => {
      if (data.length === 1 && !disableAddRemove) {
        const plan = data[0];
        const hasContent = plan.productCode || plan.productName;
        if (!hasContent) {
          onChange([]);
          // setActiveKey("0");
        }
      }
    }, []);

    const fetchProducts = async (keyword = "") => {
      if (!isMounted.current) return;
      try {
        const res = await getProductList({
          keyword,
          pageNum: 1,
          pageSize: 100,
        });
        if (isMounted.current) {
          if (keyword) {
            setProducts((prev) => {
              const newProducts = res.content || [];
              const existingCodes = new Set(prev.map((p) => p.productCode));
              const uniqueNewProducts = newProducts.filter((p) => !existingCodes.has(p.productCode));
              return [...prev, ...uniqueNewProducts];
            });
          } else {
            setProducts(res.content || []);
          }
        }
      } catch (error) {
        console.error("Failed to fetch products:", error);
      }
    };

    const updatePlan = (index: number, field: keyof OrderProductPlanVO, value: any) => {
      const newPlans = [...data];
      if (!newPlans[index]) newPlans[index] = { ...INITIAL_PRODUCT_PLAN };

      if (field === "productCode") {
        const selectedProduct = products.find((p) => p.productCode === value);
        if (selectedProduct) {
          // 获取产品详情以获取可选项
          const productOptions = {
            currencies: selectedProduct.currencies || [],
            protectionPeriods: selectedProduct.policyPeriods || [],
            paymentTerms: selectedProduct.paymentTerm || [],
            frequencies: selectedProduct.frequencies || [],
          };

          // 自动填充只有一个选项的字段
          const autoFillData: any = {
            productCode: value,
            productName: selectedProduct.productName,
            companyCode: selectedProduct.companyCode,
            companyName: selectedProduct.companyName || selectedProduct.companyCode || "",
          };

          // 币种：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.currencies.length === 1) {
            autoFillData.currency = productOptions.currencies[0];
          } else if (newPlans[index].currency && !productOptions.currencies.includes(newPlans[index].currency)) {
            autoFillData.currency = undefined;
          } else {
            autoFillData.currency = newPlans[index].currency;
          }

          // 保障期：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.protectionPeriods.length === 1) {
            autoFillData.protectionPeriod = productOptions.protectionPeriods[0];
          } else if (newPlans[index].protectionPeriod && !productOptions.protectionPeriods.includes(newPlans[index].protectionPeriod)) {
            autoFillData.protectionPeriod = undefined;
          } else {
            autoFillData.protectionPeriod = newPlans[index].protectionPeriod;
          }

          // 供款年期：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.paymentTerms.length === 1) {
            autoFillData.contributionYears = productOptions.paymentTerms[0];
          } else if (newPlans[index].contributionYears && !productOptions.paymentTerms.includes(newPlans[index].contributionYears)) {
            autoFillData.contributionYears = undefined;
          } else {
            autoFillData.contributionYears = newPlans[index].contributionYears;
          }

          // 缴费频率：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.frequencies.length === 1) {
            autoFillData.paymentFrequency = productOptions.frequencies[0];
          } else if (newPlans[index].paymentFrequency && !productOptions.frequencies.includes(newPlans[index].paymentFrequency)) {
            autoFillData.paymentFrequency = undefined;
          } else {
            autoFillData.paymentFrequency = newPlans[index].paymentFrequency;
          }

          newPlans[index] = {
            ...newPlans[index],
            ...autoFillData,
          };
        } else {
          newPlans[index] = { ...newPlans[index], [field]: value };
        }
      } else {
        newPlans[index] = { ...newPlans[index], [field]: value };
      }
      onChange(newPlans);
    };

    const getProductOptions = (productCode?: string) => {
      console.log("productCode", productCode);
      console.log("productDetailsMap", productDetailsMap);
      if (productCode && productDetailsMap[productCode]) {
        const detail = productDetailsMap[productCode];
        return {
          currencies: detail.currencies || [],
          protectionPeriods: detail.policyPeriods || [],
          paymentTerms: detail.paymentTerm || [],
          frequencies: detail.frequencies || [],
        };
      }
      const product = products.find((p) => p.productCode === productCode);
      if (product) {
        return {
          currencies: product.currencies || [],
          protectionPeriods: product.policyPeriods || [],
          paymentTerms: product.paymentTerm || [],
          frequencies: product.frequencies || [],
        };
      }
      return {
        currencies: [],
        protectionPeriods: [],
        paymentTerms: [],
        frequencies: [],
      };
    };

    const updateRider = (planIndex: number, riderIndex: number, field: keyof RiderVO, value: any) => {
      const newPlans = [...data];
      const riders = [...(newPlans[planIndex].riders || [])];
      if (!riders[riderIndex]) riders[riderIndex] = { ...INITIAL_RIDER };

      if (field === "productCode") {
        const selectedProduct = products.find((p) => p.productCode === value);

        // 无论是否在 products 中找到，都要获取产品详情
        fetchProductDetail(value);

        if (selectedProduct) {
          // 获取产品详情以获取可选项
          const productOptions = {
            currencies: selectedProduct.currencies || [],
            protectionPeriods: selectedProduct.policyPeriods || [],
            paymentTerms: selectedProduct.paymentTerm || [],
            frequencies: selectedProduct.frequencies || [],
          };

          // 自动填充只有一个选项的字段
          const autoFillData: any = {
            productCode: value,
            productName: selectedProduct.productName,
            // 附加险的保险公司必须与主险保险公司相同
            companyCode: newPlans[planIndex].companyCode,
            companyName: newPlans[planIndex].companyName || newPlans[planIndex].companyCode || "",
          };

          // 币种：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.currencies.length === 1) {
            autoFillData.currency = productOptions.currencies[0];
          } else if (riders[riderIndex].currency && !productOptions.currencies.includes(riders[riderIndex].currency)) {
            autoFillData.currency = undefined;
          } else {
            autoFillData.currency = riders[riderIndex].currency;
          }

          // 保障期：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.protectionPeriods.length === 1) {
            autoFillData.protectionPeriod = productOptions.protectionPeriods[0];
          } else if (riders[riderIndex].protectionPeriod && !productOptions.protectionPeriods.includes(riders[riderIndex].protectionPeriod)) {
            autoFillData.protectionPeriod = undefined;
          } else {
            autoFillData.protectionPeriod = riders[riderIndex].protectionPeriod;
          }

          // 供款年期：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.paymentTerms.length === 1) {
            autoFillData.contributionYears = productOptions.paymentTerms[0];
          } else if (riders[riderIndex].contributionYears && !productOptions.paymentTerms.includes(riders[riderIndex].contributionYears)) {
            autoFillData.contributionYears = undefined;
          } else {
            autoFillData.contributionYears = riders[riderIndex].contributionYears;
          }

          // 缴费频率：如果只有一个选项则自动填充，如果原有值不在新选项中则清空
          if (productOptions.frequencies.length === 1) {
            autoFillData.paymentFrequency = productOptions.frequencies[0];
          } else if (riders[riderIndex].paymentFrequency && !productOptions.frequencies.includes(riders[riderIndex].paymentFrequency)) {
            autoFillData.paymentFrequency = undefined;
          } else {
            autoFillData.paymentFrequency = riders[riderIndex].paymentFrequency;
          }

          riders[riderIndex] = {
            ...riders[riderIndex],
            ...autoFillData,
          };
        } else {
          // 产品不在 products 列表中，只设置 productCode，等待 productDetailsMap 更新后再填充其他字段
          riders[riderIndex] = {
            ...riders[riderIndex],
            productCode: value,
            // 附加险的保险公司必须与主险保险公司相同
            companyCode: newPlans[planIndex].companyCode,
            companyName: newPlans[planIndex].companyName || newPlans[planIndex].companyCode || "",
          };
        }
      } else {
        riders[riderIndex] = { ...riders[riderIndex], [field]: value };
      }

      newPlans[planIndex].riders = riders;
      onChange(newPlans);
    };

    const addRider = (planIndex: number) => {
      const newPlans = [...data];
      const riders = [...(newPlans[planIndex].riders || [])];
      riders.push({ ...INITIAL_RIDER });
      newPlans[planIndex].riders = riders;
      onChange(newPlans);
    };

    const removeRider = (planIndex: number, riderIndex: number) => {
      const newPlans = [...data];
      const riders = newPlans[planIndex].riders || [];
      newPlans[planIndex].riders = riders.filter((_, i) => i !== riderIndex);
      onChange(newPlans);
    };

    /*
  const addPlan = () => {
    const newPlans = [...data, { ...INITIAL_PRODUCT_PLAN }];
    onChange(newPlans);
    // setActiveKey(String(newPlans.length - 1));
  };

  const removePlan = (targetKey: string) => {
    const targetIndex = parseInt(targetKey);
    const newPlans = data.filter((_, i) => i !== targetIndex);
    onChange(newPlans);
    if (newPlans.length > 0) {
      // setActiveKey("0");
    }
  };

  const onEdit = (targetKey: any, action: "add" | "remove") => {
    if (action === "add") {
      addPlan();
    } else {
      removePlan(targetKey);
    }
  };
  */

    useImperativeHandle(ref, () => ({
      validate: async () => {
        if (data.length > 0) {
          const mainFormResult = await form.validateFields();

          const riderErrors: string[] = [];
          data.forEach((plan, planIndex) => {
            if (plan.riders && plan.riders.length > 0) {
              plan.riders.forEach((rider, riderIndex) => {
                if (isRiderRequired("productCode") && !rider.productCode) {
                  riderErrors.push(`產品 ${planIndex + 1} 附加保障 ${riderIndex + 1} 的投保產品不能為空`);
                }
                if (isRiderRequired("currency") && !rider.currency) {
                  riderErrors.push(`產品 ${planIndex + 1} 附加保障 ${riderIndex + 1} 的幣種不能為空`);
                }
                if (isRiderRequired("premiumPerPeriod") && !rider.premiumPerPeriod) {
                  riderErrors.push(`產品 ${planIndex + 1} 附加保障 ${riderIndex + 1} 的每期保費不能為空`);
                }
                if (isRiderRequired("sumAssured") && !rider.sumAssured) {
                  riderErrors.push(`產品 ${planIndex + 1} 附加保障 ${riderIndex + 1} 的保額不能為空`);
                }
                if (isRiderRequired("protectionPeriod") && !rider.protectionPeriod) {
                  riderErrors.push(`產品 ${planIndex + 1} 附加保障 ${riderIndex + 1} 的保障期不能為空`);
                }
                if (isRiderRequired("contributionYears") && !rider.contributionYears) {
                  riderErrors.push(`產品 ${planIndex + 1} 附加保障 ${riderIndex + 1} 的供款年期不能為空`);
                }
                if (isRiderRequired("paymentFrequency") && !rider.paymentFrequency) {
                  riderErrors.push(`產品 ${planIndex + 1} 附加保障 ${riderIndex + 1} 的繳費頻率不能為空`);
                }
              });
            }
          });

          if (riderErrors.length > 0) {
            return Promise.reject(new Error(riderErrors.join("\n")));
          }

          return mainFormResult;
        }
        return Promise.resolve();
      },
      addProductDetail: (productCode: string, detail: any) => {
        if (productCode && detail) {
          setProductDetailsMap((prev) => ({ ...prev, [productCode]: detail }));
        }
      },
    }));

    const plan = data[activeIndex];
    const index = activeIndex;

    useEffect(() => {
      if (plan) {
        // 设置主险数据
        form.setFieldsValue(plan);

        // 同步附加保障数据到表单
        if (plan.riders && plan.riders.length > 0) {
          const riderFields: Record<string, any> = {};
          plan.riders.forEach((rider, rIndex) => {
            riderFields[`rider_${index}_${rIndex}_productCode`] = rider.productCode;
            riderFields[`rider_${index}_${rIndex}_currency`] = rider.currency;
            riderFields[`rider_${index}_${rIndex}_premiumPerPeriod`] = rider.premiumPerPeriod;
            riderFields[`rider_${index}_${rIndex}_sumAssured`] = rider.sumAssured;
            riderFields[`rider_${index}_${rIndex}_protectionPeriod`] = rider.protectionPeriod;
            riderFields[`rider_${index}_${rIndex}_contributionYears`] = rider.contributionYears;
            riderFields[`rider_${index}_${rIndex}_paymentFrequency`] = rider.paymentFrequency;
          });
          form.setFieldsValue(riderFields);
        }

        // Clear validation errors for hidden conditional fields
        if (!plan.isPrepaid) {
          form.setFields([{ name: "prepaidAmountInclFirst", errors: [] }]);
        }
        if (!plan.isPolicyBackdated) {
          form.setFields([{ name: "policyBackDate", errors: [] }]);
        }
        if (!plan.applySpecialFirstYearDiscount) {
          form.setFields([
            { name: "specialFirstYearDiscountRate", errors: [] },
            { name: "commissionRate", errors: [] },
          ]);
        }
      }
    }, [plan, form, isRequired, isVisible, index]);

    if (data.length === 0) {
      return (
        <div className={styles.container} style={{ justifyContent: "center", alignItems: "center", backgroundColor: "#fff" }}>
          <div style={{ textAlign: "center" }}>
            <div style={{ marginBottom: 24, color: "#999" }}>暫無已選擇的產品投保計劃</div>
          </div>
        </div>
      );
    }

    return (
      <div className={styles.container} style={{ display: "block" }}>
        <div className={styles.content} style={{ width: "100%", padding: 0 }}>
          {plan && (
            <Form form={form} layout="vertical" disabled={readOnly}>
              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>主險/基本計劃</h3>

                  {!readOnly && !disableAddRemove && !isCorrectionMode && data.length > 0 && onRemove && (
                    <CustomButton
                      bgColor="transparent"
                      color="#ff4d4f"
                      icon={<DeleteOutlined />}
                      onClick={() => {
                        confirm({
                          title: "確認刪除",
                          content: `確定要刪除 ${plan.productName || `計劃 ${activeIndex + 1}`} 嗎？`,
                          okText: "確認",
                          cancelText: "取消",
                          onOk: () => {
                            onRemove(activeIndex);
                          },
                        });
                      }}
                      style={{ fontSize: "16px" }}
                    >
                      刪除
                    </CustomButton>
                  )}
                </div>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label="投保產品"
                      rules={[
                        {
                          required: isRequired("productCode"),
                        },
                      ]}
                      name="productCode"
                    >
                      <ProductSelectInput
                        value={plan.productCode}
                        initialName={plan.productName}
                        context={{ type: "productPlan", index }}
                        onOpenSelector={onOpenSelector}
                        productType="BASIC"
                        disabled={readOnly || (!allowMainProductSelection && isCorrectionMode)}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="保險公司"
                      rules={[
                        {
                          required: isRequired("companyName"),
                        },
                      ]}
                      name="companyName"
                    >
                      <Input placeholder="請選擇" value={plan.companyName || plan.companyCode} disabled />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="銷售地區" rules={[{ required: true }]}>
                      <Input placeholder="請選擇" value={countryOptions.find((opt) => opt.value === "HK")?.label || "香港"} disabled />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>保障及繳費</h3>
                </div>
                <Row gutter={24}>
                  <Col span={12}>
                    <Form.Item
                      label="幣種"
                      rules={[
                        {
                          required: isRequired("currency"),
                        },
                      ]}
                      name="currency"
                    >
                      <SelectInput
                        options={getProductOptions(plan.productCode).currencies.map((curr) => ({
                          label: currencyOptions.find((opt) => opt.value === curr)?.label || curr,
                          value: curr,
                        }))}
                        value={plan.currency}
                        onChange={(val) => updatePlan(index, "currency", val)}
                        title="幣種"
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="每期保費"
                      rules={[
                        {
                          required: isRequired("premiumPerPeriod"),
                        },
                      ]}
                      name="premiumPerPeriod"
                    >
                      <AmountInput
                        style={{ width: "100%" }}
                        placeholder="請輸入"
                        value={plan.premiumPerPeriod}
                        onChange={(v) => updatePlan(index, "premiumPerPeriod", v)}
                        suffix={plan.currency ? plan.currency : undefined}
                        disabled={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="保額"
                      rules={[
                        {
                          required: isRequired("sumAssured"),
                        },
                      ]}
                      name="sumAssured"
                    >
                      <AmountInput
                        style={{ width: "100%" }}
                        placeholder="請輸入"
                        value={plan.sumAssured}
                        onChange={(v) => updatePlan(index, "sumAssured", v)}
                        suffix={plan.currency ? plan.currency : undefined}
                        disabled={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="保障期"
                      rules={[
                        {
                          required: isRequired("protectionPeriod"),
                        },
                      ]}
                      name="protectionPeriod"
                    >
                      <SelectInput
                        options={getProductOptions(plan.productCode).protectionPeriods.map((period) => ({
                          label: periodOptions.find((opt) => opt.value === period)?.label || period,
                          value: period,
                        }))}
                        value={plan.protectionPeriod}
                        onChange={(val) => updatePlan(index, "protectionPeriod", val)}
                        title="保障期"
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="供款年期"
                      rules={[
                        {
                          required: isRequired("contributionYears"),
                        },
                      ]}
                      name="contributionYears"
                    >
                      <SelectInput
                        options={getProductOptions(plan.productCode).paymentTerms.map((term) => ({
                          label: term,
                          value: term,
                        }))}
                        value={plan.contributionYears}
                        onChange={(val) => updatePlan(index, "contributionYears", val)}
                        title="供款年期"
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="繳費頻率"
                      rules={[
                        {
                          required: isRequired("paymentFrequency"),
                        },
                      ]}
                      name="paymentFrequency"
                    >
                      <SelectInput
                        options={getProductOptions(plan.productCode).frequencies.map((freq) => ({
                          label: frequencyOptions.find((opt) => opt.value === freq)?.label || freq,
                          value: freq,
                        }))}
                        value={plan.paymentFrequency}
                        onChange={(val) => updatePlan(index, "paymentFrequency", val)}
                        title="繳費頻率"
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item
                      label="首期折扣後保費"
                      rules={[
                        {
                          required: isRequired("firstYearDiscountedPremium"),
                        },
                      ]}
                      name="firstYearDiscountedPremium"
                    >
                      <AmountInput
                        style={{ width: "100%" }}
                        placeholder="請輸入"
                        value={plan.firstYearDiscountedPremium}
                        onChange={(v) => updatePlan(index, "firstYearDiscountedPremium", v)}
                        suffix={plan.currency ? plan.currency : undefined}
                        disabled={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="首期繳費方法">
                      <SelectInput
                        options={firstPaymentMethodOptions}
                        value={plan.firstPaymentMethod}
                        onChange={(val) => updatePlan(index, "firstPaymentMethod", val)}
                        title="首期繳費方法"
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="續期繳費方法">
                      <SelectInput
                        options={renewalPaymentMethodOptions}
                        value={plan.renewalPaymentMethod}
                        onChange={(val) => updatePlan(index, "renewalPaymentMethod", val)}
                        title="續期繳費方法"
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                  <Col span={12}>
                    <Form.Item label="紅利分派方式">
                      <SelectInput
                        options={dividendDistributionMethodOptions}
                        value={plan.dividendDistributionMethod}
                        onChange={(val) => updatePlan(index, "dividendDistributionMethod", val)}
                        title="紅利分派方式"
                        readOnly={readOnly}
                      />
                    </Form.Item>
                  </Col>
                </Row>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>是否預繳</h3>
                  <Form.Item name="isPrepaid" valuePropName="checked" noStyle>
                    <Switch checked={!!plan.isPrepaid} onChange={(checked) => updatePlan(index, "isPrepaid", checked)} disabled={readOnly} />
                  </Form.Item>
                </div>
                {plan.isPrepaid && (
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item label="預繳額(含首期)" name="prepaidAmountInclFirst" rules={[{ required: isRequired("prepaidAmountInclFirst") }]}>
                        <AmountInput
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          value={plan.prepaidAmountInclFirst}
                          onChange={(v) => updatePlan(index, "prepaidAmountInclFirst", v)}
                          suffix={plan.currency ? plan.currency : undefined}
                          disabled={readOnly}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>是否保費融資</h3>
                  <Form.Item name="isPremiumFinanced" valuePropName="checked" noStyle>
                    <Switch checked={!!plan.isPremiumFinanced} onChange={(checked) => updatePlan(index, "isPremiumFinanced", checked)} disabled={readOnly} />
                  </Form.Item>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>是否保單回溯</h3>
                  <Form.Item name="isPolicyBackdated" valuePropName="checked" noStyle>
                    <Switch checked={!!plan.isPolicyBackdated} onChange={(checked) => updatePlan(index, "isPolicyBackdated", checked)} disabled={readOnly} />
                  </Form.Item>
                </div>
                {plan.isPolicyBackdated && (
                  <Row gutter={24}>
                    <Col span={24}>
                      <Form.Item label="保單回溯日期" name="policyBackDate" rules={[{ required: isRequired("policyBackDate") }]}>
                        <div
                          onClick={() => {
                            if (!readOnly) {
                              setDatePickerVisible({ planIndex: index });
                            }
                          }}
                          style={{
                            border: "1px solid #CBD5E1",
                            borderRadius: "6px",
                            padding: "0 16px",
                            cursor: readOnly ? "not-allowed" : "pointer",
                            display: "flex",
                            justifyContent: "space-between",
                            alignItems: "center",
                            background: readOnly ? "#f5f5f5" : "#fff",
                            height: 40,
                            width: "100%",
                          }}
                        >
                          <span
                            style={{
                              color: plan.policyBackDate ? "#334255" : "#bfbfbf",
                              fontSize: 16,
                              fontWeight: 400,
                            }}
                          >
                            {plan.policyBackDate || "請選擇"}
                          </span>
                        </div>
                        <MobileDatePicker
                          visible={datePickerVisible?.planIndex === index}
                          onClose={() => setDatePickerVisible(null)}
                          title="保單回溯日期"
                          value={plan.policyBackDate}
                          onChange={(dateString) => {
                            updatePlan(index, "policyBackDate", dateString);
                            setDatePickerVisible(null);
                          }}
                        />
                      </Form.Item>
                    </Col>
                  </Row>
                )}
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>申請客戶特殊首年保費折扣</h3>
                  <Form.Item name="applySpecialFirstYearDiscount" valuePropName="checked" noStyle>
                    <Switch checked={!!plan.applySpecialFirstYearDiscount} onChange={(checked) => updatePlan(index, "applySpecialFirstYearDiscount", checked)} disabled={readOnly} />
                  </Form.Item>
                </div>
                <Row gutter={24}>
                  {plan.applySpecialFirstYearDiscount && (
                    <Col span={12}>
                      <Form.Item label="客戶特殊首年保費折扣比例 (%)" name="specialFirstYearDiscountRate" rules={[{ required: isRequired("specialFirstYearDiscountRate") }]}>
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          value={plan.specialFirstYearDiscountRate}
                          onChange={(v) => updatePlan(index, "specialFirstYearDiscountRate", v)}
                          disabled={readOnly}
                          precision={2}
                          min={0}
                          max={100}
                          controls={false}
                        />
                      </Form.Item>
                    </Col>
                  )}
                  {plan.applySpecialFirstYearDiscount && (
                    <Col span={12}>
                      <Form.Item label="扣佣比例 (%)" name="commissionRate" rules={[{ required: isRequired("commissionRate") }]}>
                        <InputNumber
                          style={{ width: "100%" }}
                          placeholder="請輸入"
                          value={plan.commissionRate}
                          onChange={(v) => updatePlan(index, "commissionRate", v)}
                          disabled={readOnly}
                          precision={2}
                          min={0}
                          max={100}
                          controls={false}
                        />
                      </Form.Item>
                    </Col>
                  )}
                </Row>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>是否參加遞增保障權益</h3>
                  <Form.Item name="isIncreasingProtectionIncluded" valuePropName="checked" noStyle>
                    <Switch checked={!!plan.isIncreasingProtectionIncluded} onChange={(checked) => updatePlan(index, "isIncreasingProtectionIncluded", checked)} disabled={readOnly} />
                  </Form.Item>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>是否申請過DDA</h3>
                  <Form.Item name="isDdaSupported" valuePropName="checked" noStyle>
                    <Switch checked={!!plan.isDdaSupported} onChange={(checked) => updatePlan(index, "isDdaSupported", checked)} disabled={readOnly} />
                  </Form.Item>
                </div>
              </div>

              <div className={styles.card}>
                <div className={styles.cardHeader}>
                  <h3 className={styles.cardTitle}>是否需要附加保障</h3>
                  <Switch
                    checked={plan.riders && plan.riders.length > 0}
                    onChange={(checked) => {
                      if (checked && (!plan.riders || plan.riders.length === 0)) {
                        addRider(index);
                      } else if (!checked) {
                        updatePlan(index, "riders", []);
                      }
                    }}
                    disabled={readOnly}
                  />
                </div>

                {plan.riders &&
                  plan.riders.map((rider, rIndex) => (
                    <CustomCard
                      key={rIndex}
                      title={`附加保障 ${rIndex + 1}`}
                      extra={<CustomButton bgColor="transparent" color="#ff4d4f" icon={<DeleteOutlined />} onClick={() => removeRider(index, rIndex)} />}
                      className={styles.riderCard}
                    >
                      <Row gutter={24}>
                        <Col span={8}>
                          <Form.Item label="投保產品" name={`rider_${index}_${rIndex}_productCode`} rules={[{ required: isRiderRequired("productCode") }]}>
                            <ProductSelectInput
                              value={rider.productCode}
                              initialName={rider.productName}
                              context={{ type: "rider", index, rIndex }}
                              onOpenSelector={onOpenSelector}
                              productType="RIDER"
                              companyCode={plan.companyCode}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="保險公司">
                            <Input placeholder="請輸入" value={rider.companyName || rider.companyCode} disabled />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="銷售地區">
                            <Input placeholder="請輸入" value="香港" disabled />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="幣種" name={`rider_${index}_${rIndex}_currency`} rules={[{ required: isRiderRequired("currency") }]}>
                            <SelectInput
                              options={getProductOptions(rider.productCode).currencies.map((curr) => ({
                                label: currencyOptions.find((opt) => opt.value === curr)?.label || curr,
                                value: curr,
                              }))}
                              value={rider.currency}
                              onChange={(val) => updateRider(index, rIndex, "currency", val)}
                              title="幣種"
                              readOnly={readOnly}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="每期保費" name={`rider_${index}_${rIndex}_premiumPerPeriod`} rules={[{ required: isRiderRequired("premiumPerPeriod") }]}>
                            <AmountInput className={styles.fullWidth} placeholder="請輸入" value={rider.premiumPerPeriod} onChange={(v) => updateRider(index, rIndex, "premiumPerPeriod", v)} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="保額" name={`rider_${index}_${rIndex}_sumAssured`} rules={[{ required: isRiderRequired("sumAssured") }]}>
                            <AmountInput className={styles.fullWidth} placeholder="請輸入" value={rider.sumAssured} onChange={(v) => updateRider(index, rIndex, "sumAssured", v)} />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="保障期" name={`rider_${index}_${rIndex}_protectionPeriod`} rules={[{ required: isRiderRequired("protectionPeriod") }]}>
                            <SelectInput
                              options={getProductOptions(rider.productCode).protectionPeriods.map((period) => ({
                                label: periodOptions.find((opt) => opt.value === period)?.label || period,
                                value: period,
                              }))}
                              value={rider.protectionPeriod}
                              onChange={(val) => updateRider(index, rIndex, "protectionPeriod", val)}
                              title="保障期"
                              readOnly={readOnly}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="供款年期" name={`rider_${index}_${rIndex}_contributionYears`} rules={[{ required: isRiderRequired("contributionYears") }]}>
                            <SelectInput
                              options={getProductOptions(rider.productCode).paymentTerms.map((term) => ({
                                label: term,
                                value: term,
                              }))}
                              value={rider.contributionYears}
                              onChange={(val) => updateRider(index, rIndex, "contributionYears", val)}
                              title="供款年期"
                              readOnly={readOnly}
                            />
                          </Form.Item>
                        </Col>
                        <Col span={8}>
                          <Form.Item label="繳費頻率" name={`rider_${index}_${rIndex}_paymentFrequency`} rules={[{ required: isRiderRequired("paymentFrequency") }]}>
                            <SelectInput
                              options={getProductOptions(rider.productCode).frequencies.map((freq) => ({
                                label: frequencyOptions.find((opt) => opt.value === freq)?.label || freq,
                                value: freq,
                              }))}
                              value={rider.paymentFrequency}
                              onChange={(val) => updateRider(index, rIndex, "paymentFrequency", val)}
                              title="繳費頻率"
                              readOnly={readOnly}
                            />
                          </Form.Item>
                        </Col>
                      </Row>
                    </CustomCard>
                  ))}

                {plan.riders && plan.riders.length > 0 && (
                  <CustomButton bgColor="#fff" color="#666" style={{ border: "1px dashed #d9d9d9", width: "100%" }} icon={<PlusOutlined />} onClick={() => addRider(index)} block>
                    新增附加保障
                  </CustomButton>
                )}
              </div>
            </Form>
          )}
        </div>
      </div>
    );
  },
);
